import { useEffect, useId } from "react";
import type { ActionDefinition, ActionHandlerMap } from "./action-definition";
import type { ActionId } from "./action-ids";
import { useActionScope } from "./action-scope-context";
import type { ScopeNode } from "./scope-tree";

export interface UseActiveContextOptions {
  /**
   * Unique identifier for this context layer.
   * If not provided, uses React's useId().
   * Provide explicitly if you need to share context across components.
   */
  id?: string;
  /** The entity being acted upon (e.g., conversation, order) */
  context: unknown;
  /** Action handlers for this context */
  handlers: ActionHandlerMap<ActionId>;
  /** Optional: override root scope for action tree */
  rootScope?: ScopeNode<ActionId>;
  /** Optional: override getAction function */
  getAction?: (id: ActionId) => ActionDefinition<unknown, ActionId>;
  /** If false, don't push context (useful for conditional activation) */
  enabled?: boolean;
}

/**
 * Global hook to push a context layer onto the action scope stack.
 *
 * Works anywhere in the component tree - no domain-specific hooks needed.
 * Automatically cleans up on unmount.
 *
 * @example
 * // In a conversation row component
 * useActiveContext({
 *   context: conversation,
 *   handlers: useConversationActionHandlers(conversation.id),
 *   rootScope: actionMenuScope,
 *   enabled: isFocused,
 * });
 *
 * @example
 * // In a page-level component
 * useActiveContext({
 *   id: "support-page",
 *   context: null,
 *   handlers: {},
 *   rootScope: actionMenuScope,
 * });
 */
export function useActiveContext(options: UseActiveContextOptions): void {
  const autoId = useId();
  const id = options.id ?? autoId;
  const { pushContext, popContext } = useActionScope();
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    pushContext({
      id,
      context: options.context,
      handlers: options.handlers,
      rootScope: options.rootScope,
      getAction: options.getAction,
    });

    return () => {
      popContext(id);
    };
  }, [
    id,
    enabled,
    options.context,
    options.handlers,
    options.rootScope,
    options.getAction,
    pushContext,
    popContext,
  ]);
}
